/**
 * The customer's own questions, in the order they asked them, each with the
 * document that answers it.
 *
 * The data holds one row per document. The view groups rows that share a
 * question: a question with two or more documents renders once, its documents
 * listed beneath it. A question with one document stays on a single line; a
 * heading over one indented child reads worse than the plain row.
 *
 * A question with nothing mapped to it is not a failure and is not drawn as
 * one. It is a question we answered another way, and it says so.
 */

import type { PortalDocument, PortalRequestItem } from '@insuredhq/contract';

import { LOCKED_LABEL, OPENING_LABEL, OPEN_LABEL, REQUEST_HEADING, RESPONSE_PROVIDED_SEPARATELY } from '../copy';

interface RequestItemsProps {
  requestItems: PortalRequestItem[];
  documents: PortalDocument[];
  openingId: string | null;
  onOpen: (document: PortalDocument) => void;
}

interface QuestionGroup {
  question: string;
  first: PortalRequestItem;
  items: PortalRequestItem[];
}

/** Group rows by shared question text, keeping first-appearance order. */
function groupByQuestion(requestItems: PortalRequestItem[]): QuestionGroup[] {
  const groups: QuestionGroup[] = [];
  const byQuestion = new Map<string, QuestionGroup>();
  for (const item of requestItems) {
    const key = item.questionText.trim();
    const existing = byQuestion.get(key);
    if (existing === undefined) {
      const group = { question: item.questionText, first: item, items: [item] };
      byQuestion.set(key, group);
      groups.push(group);
    } else {
      existing.items.push(item);
    }
  }
  return groups;
}

export function RequestItems({ requestItems, documents, openingId, onOpen }: RequestItemsProps) {
  if (requestItems.length === 0) return null;

  const byId = new Map(documents.map((document) => [document.id, document]));

  const answerLine = (item: PortalRequestItem) => {
    const answer = item.documentId === null ? undefined : byId.get(item.documentId);
    if (answer === undefined) {
      return <p className="request-answer">{RESPONSE_PROVIDED_SEPARATELY}</p>;
    }
    return (
      <p className="request-answer">
        <span className="request-answer-title">{answer.title}</span>
        <button
          type="button"
          className="button-link"
          disabled={openingId === answer.id}
          onClick={() => onOpen(answer)}
        >
          {openingId === answer.id
            ? OPENING_LABEL
            : answer.locked
              ? LOCKED_LABEL
              : OPEN_LABEL}
        </button>
      </p>
    );
  };

  return (
    <section className="group request-card">
      <h2>{REQUEST_HEADING}</h2>
      <ul className="request-list">
        {groupByQuestion(requestItems).map((group) => (
          <li key={group.first.id} className="request-item">
            <p className="request-question">{group.question}</p>
            {group.items.length === 1 ? (
              answerLine(group.first)
            ) : (
              <ul className="request-answers">
                {group.items.map((item) => (
                  <li key={item.id}>{answerLine(item)}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
